import { call, put, select, takeLatest } from 'redux-saga/effects';
import { isBefore, subHours } from 'date-fns';

import {
  SUBMIT_USERPERSONA_MODAL,
  DISMISS_USERPERSONA_MODAL,
  showUserpersonaModal,
  getUserpersonaDetailsSuccess,
  SubmitUserpersonaAction,
  DismissUserpersonaModalAction
} from 'js/actionCreators/marketingActions';
import { AUTH_SUCCESS } from 'js/actionCreators/authActions';
import { trackPersonaAnswers, savePartialPersonaAnswers } from 'js/actionCreators/trackingActions';
import { appServices } from 'js/shared/helpers/app-services/AppServices';
import { HTTPError, MarketingAnswer } from 'js/types';

function* fetchUserpersonaResponse() {
  let userDetails = {};
  try {
    userDetails = yield appServices.getAppData('userpersona');
  } catch (error) {
    if ((error as HTTPError).httpStatusCode !== 404) {
      userDetails = { error: true };
    }
  }
  return userDetails;
}

type UserpersonaResponse = {
  answers?: MarketingAnswer;
  answerDate?: Date;
  dontShowAgain?: boolean;
  partialStep?: number;
  partialAnswers?: Partial<MarketingAnswer>;
};
function* saveUserpersonaResponse(userpersonaResponse: UserpersonaResponse) {
  try {
    yield appServices.setAppData('userpersona', userpersonaResponse);
  } catch (error) {
    console.error('Error saving userpersona response:', error);
    yield;
  }
}

export function* saveUserpersonaDismissal({ answers, step }: DismissUserpersonaModalAction) {
  const previousDismissedDate: Date | undefined = yield select(state => state.marketing.personaDismissDate);
  const dismissDate = new Date();
  const requestData = {
    dismissDate,
    ...(answers && { partialAnswers: answers }),
    ...(step && { partialStep: step }),
    ...(previousDismissedDate && { dontShowAgain: true })
  };

  yield saveUserpersonaResponse(requestData);

  // Save partial answers to Mixpanel user properties
  if (answers) {
    yield put(savePartialPersonaAnswers(answers, dismissDate));
  }
}

function* saveAnswers({ answers }: SubmitUserpersonaAction) {
  const answerDate = new Date();
  yield call(saveUserpersonaResponse, {
    answers,
    answerDate,
    dontShowAgain: true
  });
  yield put(trackPersonaAnswers(answers, answerDate, true));
}

export function* initUserpersona() {
  const { dismissDate, answerDate, error, answers, dontShowAgain, partialAnswers, partialStep } =
    yield fetchUserpersonaResponse();

  if (!error) {
    yield put(getUserpersonaDetailsSuccess({ dismissDate, dontShowAgain, answers: partialAnswers, step: partialStep }));
  }

  const hasNotErrored = !error;
  const hasNotBeenSetToNotShowAgain = !dontShowAgain;
  const hasNotAlreadyAnswered = !answerDate;
  const hasNotDismissedBefore = !dismissDate;
  const wasDismissedOver12HoursAgo = isBefore(new Date(dismissDate), subHours(Date.now(), 12));

  if (
    hasNotErrored &&
    hasNotBeenSetToNotShowAgain &&
    hasNotAlreadyAnswered &&
    (hasNotDismissedBefore || wasDismissedOver12HoursAgo)
  ) {
    yield put(showUserpersonaModal());
  }
  if (answers) {
    yield put(trackPersonaAnswers(answers, answerDate));
  }
}

function* marketingSagas() {
  yield takeLatest(AUTH_SUCCESS, initUserpersona);
  yield takeLatest(DISMISS_USERPERSONA_MODAL, saveUserpersonaDismissal);
  yield takeLatest(SUBMIT_USERPERSONA_MODAL, saveAnswers);
}

export default marketingSagas;
